import { useCallback, useEffect, useMemo, useState, type CSSProperties, type SyntheticEvent } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import { useDefaultIncompleteReportMonth } from '@/hooks/useDefaultIncompleteReportMonth'
import { useMaxMd } from '@/hooks/useMaxMd'
import {
  ActiveFilterPillBar,
  FilterBuilderDrawer,
  HoverHelp,
  SearchInput,
  SortBuilder,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  removeFilterPill,
  useColumnWidths,
  type FilterGroup,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'
import { usePageAccess } from '@/hooks/usePageAccess'
import { useQBOData } from '@/hooks/useQBOData'
import { getQboIdMappings } from '@/services/accounting'
import {
  getFixedAssetMonth,
  getFixedAssetMonthsSummary,
  type AssetBreakdownRow,
  type MonthDetailResponse,
} from '@/services/fixedAssets'
import { toast } from '@/store/toastStore'
import { useFixedAssetsNavStore } from '@/store/fixedAssetsNavStore'
import { formatApiError } from '@/utils/apiError'
import FixedAssetsJeCard from './FixedAssetsJeCard'
import { formatFixedAssetMoney, parseMoney } from './fixedAssetsFormat'
import { computeAllocateSummary, rowsWithMonthExpense } from './fixedAssetsAllocateHelpers'
import { mergeFixedAssetMonthSelectOptions } from './fixedAssetsJeStatus'
import {
  BREAKDOWN_COLUMN_KEYS,
  BREAKDOWN_COLUMN_LABELS,
  BREAKDOWN_DEFAULT_SORT,
  BREAKDOWN_DEFAULT_WIDTHS,
  BREAKDOWN_FIELD_DEFINITIONS,
  FIXED_ASSETS_ALLOCATE_BREAKDOWN_PAGE_KEY,
  PINNED_COLUMNS,
  breakdownPinnedCellClass,
  breakdownPinnedHeaderClass,
  buildFixedAssetsBreakdownFilterFields,
  fixedColumnStyle,
  isBreakdownColumnSortable,
  isBreakdownPinnedColumn,
} from './fixedAssetsAllocateTableConfig'
import {
  distinctFieldOptions,
  filterRowsByText,
  filterRowsWithTree,
  toggleSingleColumnSort,
} from './fixedAssetsTableFilters'
import { FixedAssetsSortableHeader } from './FixedAssetsSortableHeader'

function expenseTypeLabel(expenseType: string): string {
  return expenseType === 'amortization' ? 'Amortization' : 'Depreciation'
}

const EMPTY_BREAKDOWN_ROWS: AssetBreakdownRow[] = []

const BREAKDOWN_OPEN_STORAGE_KEY = 'bookkeeper-fixed-assets-allocate-breakdown-open'

function readBreakdownOpenFromStorage(): boolean {
  try {
    const raw = window.localStorage.getItem(BREAKDOWN_OPEN_STORAGE_KEY)
    if (raw === '0' || raw === 'false') return false
    return true
  } catch {
    return true
  }
}

function renderBreakdownRow(
  row: AssetBreakdownRow,
  cellStyle: (columnKey: string) => CSSProperties,
) {
  const hasTrueUp = Math.abs(parseMoney(row.true_up)) > 0.005
  return (
    <tr key={row.asset_id} className="border-t border-gray-100">
      <td
        className={`px-4 py-2 font-medium text-gray-900 ${breakdownPinnedCellClass('name')}`}
        style={cellStyle('name')}
      >
        {row.name}
      </td>
      <td className="px-4 py-2 text-gray-700" style={cellStyle('expense_type')}>
        {expenseTypeLabel(row.expense_type)}
      </td>
      <td className="px-4 py-2 text-gray-700" style={cellStyle('location')}>
        {row.location}
      </td>
      <td
        className="px-4 py-2 text-right tabular-nums text-gray-900"
        style={cellStyle('expense')}
      >
        {formatFixedAssetMoney(row.expense)}
      </td>
      <td
        className="px-4 py-2 text-right tabular-nums text-gray-900"
        style={cellStyle('true_up')}
      >
        {hasTrueUp ? (
          <span className="inline-flex items-center justify-end gap-1.5">
            {formatFixedAssetMoney(row.true_up)}
            <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900 ring-1 ring-amber-100">
              true-up
            </span>
          </span>
        ) : (
          formatFixedAssetMoney(row.true_up)
        )}
      </td>
      <td
        className="px-4 py-2 text-right tabular-nums text-gray-900"
        style={cellStyle('accum_through')}
      >
        {formatFixedAssetMoney(row.accum_through)}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-gray-900" style={cellStyle('nbv')}>
        {formatFixedAssetMoney(row.nbv)}
      </td>
    </tr>
  )
}

export default function FixedAssetsAllocate() {
  const { canEdit } = usePageAccess('fixed-assets')
  const setAllocateIncompleteCount = useFixedAssetsNavStore((s) => s.setAllocateIncompleteCount)
  const { locations } = useQBOData()
  const [monthsSummary, setMonthsSummary] = useState<Awaited<
    ReturnType<typeof getFixedAssetMonthsSummary>
  >['months']>([])
  const [monthsSummaryReady, setMonthsSummaryReady] = useState(false)
  const [monthDetail, setMonthDetail] = useState<MonthDetailResponse | null>(null)
  const [monthLoading, setMonthLoading] = useState(false)
  const [monthError, setMonthError] = useState<string | null>(null)
  const [breakdownOpen, setBreakdownOpen] = useState(readBreakdownOpenFromStorage)
  const [accountOptions, setAccountOptions] = useState<{ id: string; label: string }[]>([])
  const [textSearch, setTextSearch] = useState('')
  const [filterTree, setFilterTree] = useState<FilterGroup | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(BREAKDOWN_DEFAULT_SORT)

  const breakdownRows = useMemo(
    () => rowsWithMonthExpense(monthDetail?.asset_breakdown ?? EMPTY_BREAKDOWN_ROWS),
    [monthDetail],
  )

  const filterFields = useMemo(
    () =>
      buildFixedAssetsBreakdownFilterFields(
        distinctFieldOptions(breakdownRows.map((row) => row.location)),
      ),
    [breakdownRows],
  )

  const treeFilteredRows = useMemo(
    () => filterRowsWithTree(breakdownRows, filterTree, filterFields),
    [breakdownRows, filterTree, filterFields],
  )

  const filteredRows = useMemo(
    () => filterRowsByText(treeFilteredRows, textSearch, (row) => row.name),
    [treeFilteredRows, textSearch],
  )

  const sortedRows = useMemo(
    () => applySorts(filteredRows, sortSpecs, BREAKDOWN_FIELD_DEFINITIONS),
    [filteredRows, sortSpecs],
  )

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs],
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => toggleSingleColumnSort(prev, field))
  }, [])

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterTree, filterFields],
  )

  const activeFilterCount = useMemo(() => countFilterConditions(filterTree), [filterTree])

  const hasActiveTextSearch = textSearch.trim().length > 0
  const showingSubset =
    activeFilterCount > 0 ||
    hasActiveTextSearch ||
    sortedRows.length !== breakdownRows.length

  const handleRemoveFilterPill = useCallback(
    (pillId: string) => {
      setFilterTree(removeFilterPill(filterTree, pillId))
    },
    [filterTree],
  )

  const handleClearAllFilters = useCallback(() => {
    setFilterTree(null)
    setTextSearch('')
  }, [])

  const { getWidth, startResize, getStickyLeft } = useColumnWidths({
    pageKey: FIXED_ASSETS_ALLOCATE_BREAKDOWN_PAGE_KEY,
    columnKeys: [...BREAKDOWN_COLUMN_KEYS],
    defaults: BREAKDOWN_DEFAULT_WIDTHS,
  })
  const maxMd = useMaxMd()
  const allowColumnResize = !maxMd

  const pinnedKeys = [...PINNED_COLUMNS]

  const tableWidth = useMemo(
    () => BREAKDOWN_COLUMN_KEYS.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth],
  )

  const tableStyle = useMemo((): CSSProperties | undefined => {
    if (!allowColumnResize) return undefined
    return {
      width: tableWidth,
      minWidth: tableWidth,
      tableLayout: 'fixed',
    }
  }, [allowColumnResize, tableWidth])

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const isPinned = isBreakdownPinnedColumn(columnKey)
      const style: CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: isPinned ? 20 : 10,
      }
      if (allowColumnResize) {
        Object.assign(style, fixedColumnStyle(getWidth(columnKey)))
      }
      if (isPinned) {
        style.left = getStickyLeft(pinnedKeys, columnKey)
      }
      return style
    },
    [allowColumnResize, getStickyLeft, getWidth, pinnedKeys],
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const isPinned = isBreakdownPinnedColumn(columnKey)
      const style: CSSProperties = allowColumnResize
        ? fixedColumnStyle(getWidth(columnKey))
        : {}
      if (isPinned) {
        return {
          ...style,
          position: 'sticky',
          left: getStickyLeft(pinnedKeys, columnKey),
          zIndex: 5,
          backgroundColor: 'var(--color-white, #fff)',
        }
      }
      return style
    },
    [allowColumnResize, getStickyLeft, getWidth, pinnedKeys],
  )

  const monthSelectOptions = useMemo(
    () => mergeFixedAssetMonthSelectOptions(monthsSummary),
    [monthsSummary],
  )

  const [selectedMonth, setSelectedMonth] = useDefaultIncompleteReportMonth(
    monthSelectOptions,
    monthsSummaryReady,
  )

  const summary = useMemo(
    () => computeAllocateSummary(monthDetail?.asset_breakdown ?? []),
    [monthDetail?.asset_breakdown],
  )

  const locationOptions = useMemo(
    () => locations.map((location) => ({ id: location.id, label: location.name })),
    [locations],
  )

  const loadMonthsSummary = useCallback(async () => {
    try {
      const data = await getFixedAssetMonthsSummary()
      setMonthsSummary(data.months)
      setAllocateIncompleteCount(
        data.months.filter((month) => month.status === 'incomplete').length,
      )
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load months'))
      setMonthsSummary([])
    } finally {
      setMonthsSummaryReady(true)
    }
  }, [setAllocateIncompleteCount])

  const loadMonthDetail = useCallback(async (reportMonth: string) => {
    setMonthLoading(true)
    setMonthError(null)
    try {
      const detail = await getFixedAssetMonth(reportMonth)
      setMonthDetail(detail)
    } catch (err) {
      const message = formatApiError(err, 'Failed to load month detail')
      setMonthError(message)
      setMonthDetail(null)
      toast.error(message)
    } finally {
      setMonthLoading(false)
    }
  }, [])

  const refreshAll = useCallback(async () => {
    await loadMonthsSummary()
    if (selectedMonth) {
      await loadMonthDetail(selectedMonth)
    }
  }, [loadMonthDetail, loadMonthsSummary, selectedMonth])

  const handleBreakdownToggle = useCallback((event: SyntheticEvent<HTMLDetailsElement>) => {
    const open = event.currentTarget.open
    setBreakdownOpen(open)
    try {
      window.localStorage.setItem(BREAKDOWN_OPEN_STORAGE_KEY, open ? '1' : '0')
    } catch {
      /* ignore quota / private mode */
    }
  }, [])

  useEffect(() => {
    void loadMonthsSummary()
  }, [loadMonthsSummary])

  useEffect(() => {
    async function loadAccounts() {
      try {
        const response = await getQboIdMappings()
        setAccountOptions(
          response.mappings
            .filter((mapping) => mapping.mapping_type === 'account')
            .map((mapping) => ({ id: mapping.qbo_id, label: mapping.display_name }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        )
      } catch (err) {
        console.error('Failed to load QBO account mappings:', err)
      }
    }
    void loadAccounts()
  }, [])

  useEffect(() => {
    if (!selectedMonth) {
      setMonthDetail(null)
      return
    }
    void loadMonthDetail(selectedMonth)
  }, [loadMonthDetail, selectedMonth])

  return (
    <div className="h-full min-h-0 space-y-6 overflow-y-auto">
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            <HoverHelp label="Month" aria-label="Fixed assets month help">
              Months run from January 2025 through the last completed calendar month.
            </HoverHelp>
          </h2>
        </div>
        <div className="px-5 py-4">
          <label htmlFor="fixed-assets-month" className="sr-only">Month</label>
          <ReportMonthSelect
            id="fixed-assets-month"
            options={monthSelectOptions}
            value={selectedMonth || null}
            onChange={(id) => id && setSelectedMonth(id)}
            disabled={!monthSelectOptions.length}
            placeholder="Select month"
            className="w-[300px] max-w-full text-sm"
          />
        </div>
      </section>

      {monthLoading && !monthDetail ? (
        <p className="text-sm text-gray-500">Loading month…</p>
      ) : null}
      {monthError && !monthLoading && !monthDetail ? (
        <p className="text-sm text-red-600" role="alert">
          {monthError}
        </p>
      ) : null}

      {monthDetail ? (
        <>
          <details
            open={breakdownOpen}
            onToggle={handleBreakdownToggle}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <summary
              className="flex flex-wrap cursor-pointer list-none items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 text-base font-semibold text-gray-900 [&::-webkit-details-marker]:hidden"
            >
              <span>Per-asset breakdown</span>
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            </summary>
            <div>
              <div className="flex flex-col gap-4 border-b border-gray-200 p-4 lg:flex-row lg:items-center lg:justify-between">
                <SearchInput
                  value={textSearch}
                  onChange={setTextSearch}
                  placeholder="Search assets…"
                  className="max-w-md flex-1"
                />
                <TableToolbar
                  filterCount={activeFilterCount}
                  sortCount={sortSpecs.length}
                  showFilters={showFilters}
                  showSort={showSort}
                  onToggleFilters={() => setShowFilters((open) => !open)}
                  onToggleSort={() => setShowSort((open) => !open)}
                />
              </div>
              <ActiveFilterPillBar
                pills={filterPills}
                onRemovePill={handleRemoveFilterPill}
                onClearAll={handleClearAllFilters}
              />
              {showingSubset ? (
                <div className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
                  <span className="font-medium text-gray-700">
                    {sortedRows.length} asset{sortedRows.length !== 1 ? 's' : ''} shown
                  </span>
                  <span className="ml-1">(of {breakdownRows.length.toLocaleString()} total)</span>
                </div>
              ) : null}
              <div className="table-scroll-mobile overflow-x-auto">
                <table
                  className={`table-forecast border-separate border-spacing-0 text-sm${allowColumnResize ? '' : ' min-w-full'}`}
                  style={tableStyle}
                >
                  <thead>
                    <tr>
                      {BREAKDOWN_COLUMN_KEYS.map((columnKey) => {
                        const isRightAligned =
                          columnKey === 'expense' ||
                          columnKey === 'true_up' ||
                          columnKey === 'accum_through' ||
                          columnKey === 'nbv'
                        const label =
                          columnKey === 'true_up' ? (
                            <HoverHelp label="True-up" aria-label="True-up column help">
                              Past posted months are never rewritten. The next journal entry
                              self-corrects using expected minus booked amounts.
                            </HoverHelp>
                          ) : (
                            BREAKDOWN_COLUMN_LABELS[columnKey]
                          )

                        return (
                          <FixedAssetsSortableHeader
                            key={columnKey}
                            label={label}
                            sortKey={columnKey}
                            sortDirection={
                              isBreakdownColumnSortable(columnKey)
                                ? getSortDirection(columnKey)
                                : null
                            }
                            onSort={handleRequestSort}
                            className={`px-4 py-2 ${isRightAligned ? 'text-right' : 'text-left'} ${breakdownPinnedHeaderClass(columnKey)}`}
                            columnKey={columnKey}
                            style={headerStyle(columnKey)}
                            onStartResize={allowColumnResize ? startResize : undefined}
                            showResize={allowColumnResize}
                          />
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={BREAKDOWN_COLUMN_KEYS.length}
                          className="px-4 py-8 text-center text-sm text-gray-500"
                        >
                          {breakdownRows.length === 0 ? (
                            'No assets with month expense this month.'
                          ) : (
                            <>
                              No assets match your filters.{' '}
                              <button
                                type="button"
                                className="font-medium text-blue-600 hover:underline"
                                onClick={handleClearAllFilters}
                              >
                                Clear filters
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ) : (
                      sortedRows.map((row) => renderBreakdownRow(row, cellStyle))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-gray-200 px-4 py-4">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3 lg:grid-cols-5">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Total depreciation
                    </dt>
                    <dd className="mt-0.5 tabular-nums font-medium text-gray-900">
                      {formatFixedAssetMoney(summary.totalDepreciation)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Total amortization
                    </dt>
                    <dd className="mt-0.5 tabular-nums font-medium text-gray-900">
                      {formatFixedAssetMoney(summary.totalAmortization)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Grand total
                    </dt>
                    <dd className="mt-0.5 tabular-nums font-semibold text-gray-900">
                      {formatFixedAssetMoney(summary.grandTotal)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Assets contributing
                    </dt>
                    <dd className="mt-0.5 tabular-nums font-medium text-gray-900">
                      {summary.contributingAssetCount}
                    </dd>
                  </div>
                  {Math.abs(summary.trueUpTotal) > 0.005 ? (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        <HoverHelp label="True-up" aria-label="True-up help">
                          Past posted months are never rewritten. The next journal entry self-corrects
                          using expected minus booked amounts.
                        </HoverHelp>
                      </dt>
                      <dd className="mt-0.5 tabular-nums font-medium text-gray-900">
                        {formatFixedAssetMoney(summary.trueUpTotal)}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </div>
            </div>
          </details>

          <FixedAssetsJeCard
            monthDetail={monthDetail}
            loading={monthLoading}
            error={monthError}
            canEdit={canEdit}
            summary={summary}
            accountOptions={accountOptions}
            locationOptions={locationOptions}
            onChanged={refreshAll}
          />

          <FilterBuilderDrawer
            open={showFilters}
            onClose={() => setShowFilters(false)}
            fields={filterFields}
            root={filterTree}
            onChange={setFilterTree}
            onClearAll={() => setFilterTree(null)}
          />

          <SortBuilder
            open={showSort}
            fields={BREAKDOWN_FIELD_DEFINITIONS}
            sorts={sortSpecs}
            onChange={setSortSpecs}
            onClose={() => setShowSort(false)}
            onClearAll={() => setSortSpecs(BREAKDOWN_DEFAULT_SORT)}
          />
        </>
      ) : null}
    </div>
  )
}
